import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';

export interface VoidRecord {
  order_id: string;
  order_number: string;
  amount: number;
  reason: string;
  approved_by: string;
  approved_by_name: string;
  voided_at: string;
}

export interface RefundRecord {
  order_id: string;
  order_number: string;
  amount: number;
  reason: string;
  approved_by: string;
  approved_by_name: string;
  refunded_at: string;
}

export interface CashMovement {
  id: string;
  amount: number;
  movement_type: 'opening_float' | 'pay_in' | 'pay_out' | 'closing_float' | 'cash_to_bank';
  reason: string | null;
  created_at: string;
}

export interface ItemSold {
  name: string;
  quantity: number;
  total: number;
}

// Payment type breakdown within a payment method
export interface PaymentTypeBreakdown {
  type_id: string | null;
  type_name: string;
  total: number;
  count: number;
}

// Payment method summary with type breakdown
export interface PaymentMethodSummary {
  total: number;
  count: number;
  tip_total?: number;
  types: PaymentTypeBreakdown[];
}

// Full payment breakdown by method
export type PaymentBreakdown = Record<string, PaymentMethodSummary>;

export interface SettlementData {
  session_id: string;
  register_name: string;
  cashier_name: string;
  location_name: string;
  session_type: 'register' | 'virtual';
  opened_at: string;
  closed_at: string | null;
  opening_balance: number;
  closing_balance: number;
  expected_balance: number;
  variance: number;
  cash_payments: number;
  cash_refunds: number;
  pay_ins: number;
  pay_outs: number;
  void_count: number;
  void_amount: number;
  voids_summary: VoidRecord[];
  discount_count: number;
  discount_amount: number;
  refund_count: number;
  refund_amount: number;
  refunds_summary: RefundRecord[];
  payment_summary: Record<string, { expected: number; counted: number }>;
  payment_breakdown: PaymentBreakdown;
  items_sold_summary: ItemSold[];
  denominations: Record<string, number>;
  total_orders: number;
  completed_orders: number;
  gross_sales: number;
  net_sales: number;
  tax_collected: number;
}

export interface OpenRegisterSession {
  id: string;
  register_id: string | null;
  register_name: string;
  user_id: string;
  user_name: string;
  session_type: 'register' | 'virtual';
  opened_at: string;
  opening_balance: number;
  cash_payments: number;
  void_count: number;
  void_amount: number;
  order_count: number;
  total_sales: number;
}

export function useRegisterSettlement() {
  const { currentLocation } = useLocationStore();
  const queryClient = useQueryClient();

  // Fetch all open register sessions at the current location (for supervisors)
  // Uses the new get_location_open_sessions function that includes virtual sessions
  const openSessionsQuery = useQuery({
    queryKey: ['open-register-sessions', currentLocation?.id],
    queryFn: async () => {
      if (!currentLocation?.id) return [];

      const { data, error } = await supabase.rpc('get_location_open_sessions', {
        p_location_id: currentLocation.id,
      });

      if (error) {
        console.error('Error fetching open sessions:', error);
        throw error;
      }

      return (data || []).map((session: any) => ({
        id: session.id,
        register_id: session.register_id,
        register_name: session.register_name || 'Virtual Session',
        user_id: session.user_id,
        user_name: session.user_name || 'Unknown',
        session_type: session.session_type || 'register',
        opened_at: session.opened_at,
        opening_balance: Number(session.opening_balance) || 0,
        cash_payments: Number(session.cash_payments) || 0,
        void_count: session.void_count || 0,
        void_amount: Number(session.void_amount) || 0,
        order_count: Number(session.order_count) || 0,
        total_sales: Number(session.total_sales) || 0,
      })) as OpenRegisterSession[];
    },
    enabled: !!currentLocation?.id,
    refetchInterval: 30000, // Refresh every 30 seconds for real-time view
  });

  // Fetch settlement data for a specific session using enhanced v2 function
  const fetchSettlementData = async (sessionId: string): Promise<SettlementData | null> => {
    // Try v2 first (with payment breakdown), fall back to v1 if not available
    let data, error;
    
    ({ data, error } = await supabase.rpc('get_settlement_summary_v2', {
      p_session_id: sessionId,
    }));

    // Fall back to original function if v2 doesn't exist
    if (error?.code === '42883') { // Function doesn't exist
      ({ data, error } = await supabase.rpc('get_settlement_summary', {
        p_session_id: sessionId,
      }));
    }

    if (error) {
      console.error('Error fetching settlement summary:', error);
      return null;
    }

    if (!data || data.length === 0) return null;

    const row = data[0];
    return {
      session_id: row.session_id,
      register_name: row.register_name || 'Virtual Session',
      cashier_name: row.cashier_name || 'Unknown',
      location_name: row.location_name || 'Unknown',
      session_type: row.session_type || 'register',
      opened_at: row.opened_at,
      closed_at: row.closed_at,
      opening_balance: Number(row.opening_balance) || 0,
      closing_balance: Number(row.closing_balance) || 0,
      expected_balance: Number(row.expected_balance) || 0,
      variance: Number(row.variance) || 0,
      cash_payments: Number(row.cash_payments) || 0,
      cash_refunds: Number(row.cash_refunds) || 0,
      pay_ins: Number(row.pay_ins) || 0,
      pay_outs: Number(row.pay_outs) || 0,
      void_count: row.void_count || 0,
      void_amount: Number(row.void_amount) || 0,
      voids_summary: row.voids_summary || [],
      discount_count: row.discount_count || 0,
      discount_amount: Number(row.discount_amount) || 0,
      refund_count: row.refund_count || 0,
      refund_amount: Number(row.refund_amount) || 0,
      refunds_summary: row.refunds_summary || [],
      payment_summary: row.payment_summary || {},
      payment_breakdown: row.payment_breakdown || {},
      items_sold_summary: row.items_sold_summary || [],
      denominations: row.denominations || {},
      total_orders: row.total_orders || 0,
      completed_orders: row.completed_orders || 0,
      gross_sales: Number(row.gross_sales) || 0,
      net_sales: Number(row.net_sales) || 0,
      tax_collected: Number(row.tax_collected) || 0,
    };
  };

  // Settle a register session (closes session and logs out cashier)
  const settleSession = useMutation({
    mutationFn: async ({
      sessionId,
      closingBalance,
      denominations,
      paymentTypeCounted,
      approvedBy,
    }: {
      sessionId: string;
      closingBalance: number;
      denominations: Record<string, number>;
      paymentTypeCounted?: Record<string, number>; // { payment_type_id: counted_amount }
      approvedBy: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Try v2 first (enhanced with payment type tracking), fall back to v1
      let data, error;
      
      ({ data, error } = await supabase.rpc('settle_register_session_v2', {
        p_session_id: sessionId,
        p_closing_balance: closingBalance,
        p_denominations: denominations,
        p_payment_type_counted: paymentTypeCounted || {},
        p_settled_by: user.id,
        p_approved_by: approvedBy,
      }));

      // Fall back to original function if v2 doesn't exist
      if (error?.code === '42883') {
        ({ data, error } = await supabase.rpc('settle_register_session', {
          p_session_id: sessionId,
          p_closing_balance: closingBalance,
          p_denominations: denominations,
          p_settled_by: user.id,
          p_approved_by: approvedBy,
        }));
      }

      if (error) throw error;

      // Log audit event
      await supabase.from('audit_logs').insert({
        user_id: user.id,
        location_id: currentLocation?.id,
        action: 'settle_register',
        entity_type: 'cash_drawer_session',
        entity_id: sessionId,
        changes: {
          closing_balance: closingBalance,
          settled_by: user.id,
          approved_by: approvedBy,
          payment_type_counted: paymentTypeCounted,
        },
      });

      return data;
    },
    onSuccess: () => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['open-register-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['register-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['register-session'] });
      queryClient.invalidateQueries({ queryKey: ['user-active-session'] });
      queryClient.invalidateQueries({ queryKey: ['register-closures'] });
    },
  });

  // Query for a specific session's real-time settlement data
  const useSettlementDataQuery = (sessionId: string | null) => {
    return useQuery({
      queryKey: ['settlement-data', sessionId],
      queryFn: () => sessionId ? fetchSettlementData(sessionId) : null,
      enabled: !!sessionId,
      staleTime: 1000 * 10, // 10 seconds
      refetchInterval: 1000 * 30, // Refresh every 30 seconds
    });
  };

  return {
    // Open sessions at location
    openSessions: openSessionsQuery.data || [],
    isLoadingSessions: openSessionsQuery.isLoading,
    refetchOpenSessions: openSessionsQuery.refetch,
    
    // Settlement data
    fetchSettlementData,
    useSettlementDataQuery,
    
    // Settlement action
    settleSession,
    isSettling: settleSession.isPending,
  };
}

// Helper to format payment method name for display
function formatMethodName(method: string): string {
  // Common payment method labels
  const methodLabels: Record<string, string> = {
    cash: 'Cash',
    card: 'Card',
    gift_card: 'Gift Card',
    loyalty: 'Loyalty Points',
    store_credit: 'Store Credit',
    other: 'Other',
  };

  // Return predefined label if exists
  if (methodLabels[method]) {
    return methodLabels[method];
  }

  // Otherwise, format the method name nicely (replace underscores, capitalize words)
  return method
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

// Helper to format payment breakdown for display
export function formatPaymentBreakdown(breakdown: PaymentBreakdown): Array<{
  method: string;
  methodLabel: string;
  total: number;
  count: number;
  types: PaymentTypeBreakdown[];
}> {
  return Object.entries(breakdown).map(([method, data]) => ({
    method,
    methodLabel: formatMethodName(method),
    total: data.total,
    count: data.count,
    types: data.types || [],
  }));
}
